import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from "react-router-dom"
import { useCallback, useContext, useRef } from "react"
import { useFormikContext } from "formik"
import { FormContext } from "../../context/formContext"
import { Button } from "primereact/button"
import { classNames } from "primereact/utils"
import { Toast } from "primereact/toast"
import { confirmDialog } from "primereact/confirmdialog"

import styles from "../../styles/Nav-Blocks.module.css"
import {
  getFormValidationToast,
  getPrefix,
  smoothScrollToTop,
} from "./helpers/FormNavHelpers"

export default function BlockActionBtns({ block }) {
  const formik = useFormikContext()
  const form = useContext(FormContext)
  const toast = useRef(null)
  const location = useLocation()
  const previousUrl = location.pathname
  const navigate = useNavigate()
  const { idTemplate, formPage } = useParams()

  // For validation, before saving and navigating
  const currentBlockFields = form.blocksFetched[block]?.fields
  const isRequired = form.blocksRequired.some((x) => x === block)
  const isEdit = form.blocksEdited[block]
  const isSaved = form.blocksSaved[block]
  const isDirty = formik.dirty

  const resetForm = useCallback(() => {
    formik.resetForm({ values: formik.initialValues })
  }, [formik])

  const showResetConfirmation = useCallback(() => {
    confirmDialog({
      message:
        "Are you sure you want to reset this block? All unsaved changes will be lost.",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      accept: resetForm,
    })
  }, [resetForm])

  const handlePageJump = async (forward, skipBlock) => {
    if (!skipBlock) {
      // Determine whether we should run validation before navigating:
      // - If moving forward and this is a required block => always validate
      // - Otherwise validate only when the form is dirty
      const shouldValidate = forward ? (isRequired ? true : isDirty) : isDirty
      if (shouldValidate) {
        // Validate form before navigating
        const errors = await formik.validateForm()
        if (Object.keys(errors).length > 0) {
          formik.submitForm() // to trigger error display in UI
          if (toast.current && currentBlockFields) {
            toast.current.show(
              getFormValidationToast(errors, currentBlockFields),
            )
          }

          // Stop navigation
          return
        }
      }

      // Determine whether we should save the block:
      // - If moving forward, always save the block (this marks it as 'saved' in the nav).
      // - If moving backward, save only if the form is dirty.
      const shouldSave = forward ? true : isDirty
      if (shouldSave) {
        // Save current block before navigating
        form.handleSaveBlock(block, formik.values)
      }
    }

    const destPage = () => {
      // determine next page number
      const nextPage = forward ? Number(formPage) + 1 : Number(formPage) - 1
      const prefix = getPrefix(idTemplate, previousUrl)

      // build the destination path based on edit mode
      const pattern = form.editMode
        ? `${prefix}/:formPage/${form.idStamp}`
        : `${prefix}/:formPage/`

      return generatePath(pattern, { formPage: nextPage })
    }

    navigate(destPage(), { replace: true })

    // Smooth scroll to top of form
    smoothScrollToTop()
  }

  return (
    <div className={styles.wrapper}>
      <Toast ref={toast} />

      {form.blocksSaved[block] && (
        <Button
          type="button"
          label="Edit"
          icon="pi pi-pencil"
          iconPos="left"
          className={classNames(styles.editBtn, "blockActionButton")}
          onClick={() => form.handleEditBlock(block)}
        />
      )}

      {/* Reset button when form is dirty */}
      {isDirty && (
        <Button
          type="button"
          label="Reset"
          icon="pi pi-refresh"
          iconPos="left"
          className={classNames(styles.editBtn, "blockActionButton")}
          onClick={showResetConfirmation}
        />
      )}

      {!block.isLastBlock && (
        <>
          {!isDirty && !isRequired && !isEdit && !isSaved ? (
            <Button
              type="button"
              label="Skip Optional Block"
              icon="pi pi-angle-double-right"
              iconPos="right"
              className={classNames(styles.editBtn, "blockActionButton")}
              onClick={() => handlePageJump(true, true)}
            />
          ) : (
            <Button
              type="button"
              label="Next"
              icon="pi pi-angle-double-right"
              iconPos="right"
              className={classNames(styles.navBtn, "blockActionButton")}
              onClick={() => handlePageJump(true, false)}
            />
          )}
        </>
      )}
    </div>
  )
}
